<template>
  <VueFinalModal
    content-transition="vfm-fade"
    :click-to-close="true"
    class="flex justify-center items-center p-6"
    content-class="relative w-full max-w-6xl h-[85vh] bg-[#fafbfc] rounded-[40px] shadow-2xl flex overflow-hidden z-50 ring-1 ring-black/5"
    @close="close"
  >
    <div class="flex w-full h-full min-h-0">
      <AudioContentPreviewSidebar
        :cover-image-url="sidebarCoverUrl"
        :title="form.title"
        :author-name="sidebarAuthorName"
        :type="form.type"
        :audio-url="sidebarAudioUrl"
      />

      <div class="flex-1 flex flex-col bg-[#fafbfc] relative overflow-hidden min-w-0">
        <header class="px-8 py-6 border-b border-[#1c4848]/5 flex items-center justify-between bg-white/50 backdrop-blur-sm sticky top-0 z-30">
          <h2 class="text-xl font-bold text-[#1c4848] tracking-tight">Create Content</h2>
          <div class="flex gap-3">
            <button type="button" class="px-4 py-2 rounded-xl text-xs font-bold text-[#1c4848]/60 hover:text-[#1c4848] hover:bg-[#1c4848]/5 transition-colors" @click="close">Cancel</button>
            <button
              type="button"
              :disabled="!canSave"
              class="px-6 py-2 rounded-xl text-xs font-bold text-white bg-[#1c4848] shadow-lg shadow-[#1c4848]/20 hover:bg-[#153838] active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="save"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              Save & Publish
            </button>
          </div>
        </header>

        <div class="flex-1 overflow-y-auto custom-scrollbar p-8">
          <form @submit.prevent="save" class="space-y-8 max-w-3xl mx-auto">
            <section>
              <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-widest mb-3 ml-1">Select Content Type</label>
              <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
                <button type="button" class="type-btn relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center gap-2 transition-all" :class="form.type === 'nasheed' ? 'border-[#1c4848] bg-[#1c4848] text-white shadow-md' : 'border-[#1c4848]/5 bg-white text-[#1c4848]/60 hover:border-[#1c4848]/20 hover:text-[#1c4848]'" @click="form.type = 'nasheed'">
                  <div class="w-10 h-10 rounded-full flex items-center justify-center" :class="form.type === 'nasheed' ? 'bg-white/10' : 'bg-[#1c4848]/5'"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg></div>
                  <span class="text-xs font-bold">Islamic Nasheed</span>
                </button>
                <button type="button" class="type-btn relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center gap-2 transition-all" :class="form.type === 'story' ? 'border-[#1c4848] bg-[#1c4848] text-white shadow-md' : 'border-[#1c4848]/5 bg-white text-[#1c4848]/60 hover:border-[#1c4848]/20 hover:text-[#1c4848]'" @click="form.type = 'story'">
                  <div class="w-10 h-10 rounded-full flex items-center justify-center" :class="form.type === 'story' ? 'bg-white/10' : 'bg-[#1c4848]/5'"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg></div>
                  <span class="text-xs font-bold">Islamic Story</span>
                </button>
                <button type="button" class="type-btn relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center gap-2 transition-all" :class="form.type === 'calm_sound' ? 'border-[#1c4848] bg-[#1c4848] text-white shadow-md' : 'border-[#1c4848]/5 bg-white text-[#1c4848]/60 hover:border-[#1c4848]/20 hover:text-[#1c4848]'" @click="form.type = 'calm_sound'">
                  <div class="w-10 h-10 rounded-full flex items-center justify-center" :class="form.type === 'calm_sound' ? 'bg-white/10' : 'bg-[#1c4848]/5'"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="M2 12h2"/><path d="M19.07 4.93l-1.41 1.41"/><path d="M22 12h-2"/><path d="M6.34 17.66l-1.41 1.41"/><path d="M12 20v2"/><path d="M17.66 17.66l1.41 1.41"/><path d="M12 6a6 6 0 0 1 6 6c0 2.22-1.2 4.16-3 5.2V20h-2v-3h-2v3H9v-2.8c-1.8-1.04-3-2.98-3-5.2a6 6 0 0 1 6-6z"/></svg></div>
                  <span class="text-xs font-bold">Calm Sound</span>
                </button>
              </div>
            </section>

            <section class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div class="md:col-span-2">
                <TextInput v-model="form.title" label="Title" placeholder="Enter content title..." input-id="audio-content-title" />
              </div>
              <div>
                <SelectInput v-model="form.authorId" label="Author / Artist" select-id="audio-content-author" :options="authorOptions" placeholder="Select Author" />
              </div>
              <div>
                <SelectInput v-model="form.languageId" label="Language" select-id="audio-content-language" :options="languageOptions" />
              </div>
              <div>
                <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Audience</label>
                <select v-model="form.audience" class="w-full bg-white border border-[#1c4848]/10 rounded-xl px-4 py-3 text-sm font-semibold text-[#1c4848] outline-none cursor-pointer">
                  <option value="adults">Adults</option>
                  <option value="kids">Kids</option>
                </select>
              </div>
              <template v-if="form.audience === 'kids'">
                <div>
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Age from</label>
                  <input v-model.number="form.ageFrom" type="number" min="0" max="18" placeholder="0" class="w-full bg-white border border-[#1c4848]/10 rounded-xl px-4 py-3 text-sm font-semibold text-[#1c4848] outline-none">
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Age to (optional)</label>
                  <input v-model.number="form.ageTo" type="number" min="0" max="18" placeholder="e.g. 5 or leave empty for +" class="w-full bg-white border border-[#1c4848]/10 rounded-xl px-4 py-3 text-sm font-semibold text-[#1c4848] outline-none">
                </div>
              </template>
              <div class="grid grid-cols-2 gap-3" :class="form.audience === 'kids' ? '' : 'md:col-span-2'">
                <div>
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Rating</label>
                  <input v-model.number="form.rating" type="number" step="0.1" min="0" max="5" placeholder="5.0" class="w-full bg-white border border-[#1c4848]/10 rounded-xl px-4 py-3 text-sm font-semibold text-[#1c4848] outline-none">
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Sort Order</label>
                  <input v-model.number="form.order" type="number" placeholder="0" class="w-full bg-white border border-[#1c4848]/10 rounded-xl px-4 py-3 text-sm font-semibold text-[#1c4848] outline-none">
                </div>
              </div>
              <div class="md:col-span-2">
                <TextareaInput v-model="form.description" label="Description" placeholder="Write a brief description..." :rows="3" textarea-id="audio-content-desc" />
              </div>
            </section>

            <section class="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div class="md:col-span-1">
                <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1 shrink-0">Cover Image</label>
                <div class="w-full aspect-square shrink-0">
                  <ImageDropzone v-model="form.coverFile" input-id="audio-content-cover" size="small" class="h-full w-full" />
                </div>
              </div>
              <div class="md:col-span-2 flex flex-col">
                <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1 shrink-0">Audio File</label>
                <div class="flex-1 min-h-0 flex flex-col">
                  <AudioDropzone v-model="form.audioFile" input-id="audio-content-audio" class="flex-1 min-h-0" />
                </div>
              </div>
            </section>
          </form>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import AudioContentPreviewSidebar from './AudioContentPreviewSidebar.vue';
import TextInput from '@/components/forms/text/TextInput.vue';
import SelectInput from '@/components/forms/select/SelectInput.vue';
import TextareaInput from '@/components/forms/textarea/TextareaInput.vue';
import ImageDropzone from '@/components/forms/dropzone/ImageDropzone.vue';
import AudioDropzone from '@/components/forms/dropzone/AudioDropzone.vue';
import { useAudioContentsStore } from '@/store/audio-contents/index.js';
import { useAuthorsStore } from '@/store/authors/index.js';
import { useLanguagesStore } from '@/store/languages/index.js';
import { getLanguageLabel } from '@/utils/languageLabel.js';

const props = defineProps({
  onClose: { type: Function, default: null },
  onCreated: { type: Function, default: null },
});
const emit = defineEmits(['close', 'created']);

const audioStore = useAudioContentsStore();
const authorsStore = useAuthorsStore();
const languagesStore = useLanguagesStore();

const form = ref({
  title: '',
  description: '',
  type: 'story',
  rating: null,
  duration: null,
  order: 0,
  authorId: '',
  languageId: '',
  audience: 'adults',
  ageFrom: null,
  ageTo: null,
  coverFile: null,
  audioFile: null,
});

onMounted(() => {
  Promise.all([authorsStore.fetchAuthors(), languagesStore.fetchLanguages()]);
});

const authorOptions = computed(() => {
  const list = authorsStore.authors ?? [];
  return [
    { value: '', label: 'Select Author' },
    ...list.map((a) => ({ value: String(a.id), label: a.nickname ?? a.name ?? '—' })),
  ];
});

const languageOptions = computed(() => {
  const list = languagesStore.languages ?? [];
  return [
    { value: '', label: 'Select language' },
    ...list.map((l) => ({ value: String(l.id), label: getLanguageLabel(l) })),
  ];
});

const coverObjectUrl = ref('');
const audioObjectUrl = ref('');
watch(() => form.value.coverFile, (f) => {
  if (coverObjectUrl.value) URL.revokeObjectURL(coverObjectUrl.value);
  coverObjectUrl.value = f instanceof File ? URL.createObjectURL(f) : '';
}, { immediate: true });
watch(() => form.value.audioFile, (f) => {
  if (audioObjectUrl.value) URL.revokeObjectURL(audioObjectUrl.value);
  audioObjectUrl.value = f instanceof File ? URL.createObjectURL(f) : '';
}, { immediate: true });
onBeforeUnmount(() => {
  if (coverObjectUrl.value) URL.revokeObjectURL(coverObjectUrl.value);
  if (audioObjectUrl.value) URL.revokeObjectURL(audioObjectUrl.value);
});

const sidebarCoverUrl = computed(() => coverObjectUrl.value);
const sidebarAudioUrl = computed(() => audioObjectUrl.value);
const sidebarAuthorName = computed(() => {
  const id = form.value.authorId;
  if (!id) return 'Author Name';
  const author = authorsStore.authors?.find((a) => String(a.id) === id);
  return author?.nickname ?? author?.name ?? 'Author';
});

const canSave = computed(() => {
  const base = form.value.title?.trim() && form.value.coverFile instanceof File && form.value.audioFile instanceof File && form.value.languageId;
  if (form.value.audience !== 'kids') return base;
  const from = form.value.ageFrom;
  return base && from !== null && from !== '' && Number(from) >= 0 && Number(from) <= 18;
});

function close() {
  if (props.onClose) props.onClose();
  else emit('close');
}
async function save() {
  if (!canSave.value) return;
  try {
    const payload = {
      title: form.value.title.trim(),
      description: form.value.description?.trim() || null,
      type: form.value.type,
      rating: form.value.rating ?? null,
      duration: form.value.duration ?? null,
      order: form.value.order ?? 0,
      author_id: form.value.authorId || null,
      language_id: form.value.languageId ? Number(form.value.languageId) : null,
      audience: form.value.audience,
      age_from: form.value.audience === 'kids' ? (form.value.ageFrom != null && form.value.ageFrom !== '' ? Number(form.value.ageFrom) : null) : null,
      age_to: form.value.audience === 'kids' ? (form.value.ageTo != null && form.value.ageTo !== '' ? Number(form.value.ageTo) : null) : null,
    };
    const created = await audioStore.create(payload);
    await audioStore.uploadCover(created.id, form.value.coverFile);
    await audioStore.uploadAudio(created.id, form.value.audioFile);
    if (props.onCreated) props.onCreated();
    else emit('created');
    close();
  } catch (err) {
    console.error(err);
    alert('Failed to save. Please try again.');
  }
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(28, 72, 72, 0.2); border-radius: 3px; }
</style>
